import * as React from "react";
import { LungVolumeResults, StudyLungVolumeRelevantParameters } from '../Shared/PFTAPITypes';
import { LocalizedDateTime, DateTimeAccuracy } from '../../misc/components/LocalizedDateTime';
import { PFTResultParameters } from '../Shared/PFTResultsParameters';
import { TestAcceptabilityTable } from '../Shared/TestAcceptabilityTable';
import { AcceptabilityState, ToggleTestAcceptabilityAction } from '../Shared/UseAcceptability';
import RepeatabilityTable from "../Shared/RepeatabilityTable";
import { LungVolumeSession } from "webportalclient";
import { NextGenFixedCommentsState } from "../Spirometry/Testing/UseNextGenFixedComments";
import { ResultsContext } from "../Spirometry/UseResultsContext";
import { OverreadingResultsComments } from "../Shared/OverreadingResultsComments";

export interface LungVolumeResultsTableProps {
    isViewOnly: boolean;
    isRejected: boolean;
    isSiteAnalysisSet: boolean;
    resultData: LungVolumeResults;
    session: LungVolumeSession;
    fixedCommentsStateOverreading: NextGenFixedCommentsState;
    fixedCommentsDispatchOverreading;
    acceptabilityState: AcceptabilityState;
    acceptabilityDispatch;
    shouldValidateFixedComments: boolean;
    studyLungVolumeRelevantParameters?: StudyLungVolumeRelevantParameters;
}

export const LungVolumeResultsTable = ({ isViewOnly, isRejected, isSiteAnalysisSet, resultData, session, studyLungVolumeRelevantParameters, fixedCommentsStateOverreading, fixedCommentsDispatchOverreading, shouldValidateFixedComments, acceptabilityState, acceptabilityDispatch }: LungVolumeResultsTableProps) => {
    return (
        <>
            <ResultsContext.Provider value={{ showBestValues: true, showPredictedValues: false, showPercentagePredicted: false, showReversibilityBaseValue: false, showReversibilityAbsoluteChange: false, showReversibilityPercentageChange: false }}>
                <table id="LVResultsTable" className="table table-striped">
                    <thead>
                        <tr>
                            <th></th>
                            {
                                resultData.TestResults.sort((a, b) => a.TestNumber - b.TestNumber).map(t =>
                                    <th key={`TestHeader${t.TestId}`} style={{ color: "#000" }}>{t.TestNumber}</th>
                                )}
                            <th>Best</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Time</td>
                            {
                                resultData.TestResults.sort((a, b) => a.TestNumber - b.TestNumber).map(t =>
                                    <td key={`TestDateTime${t.TestId}`} data-automationid={`test-occurred-${t.TestNumber}`}>
                                        <LocalizedDateTime time={t.TestDateTime} accuracy={DateTimeAccuracy.Second} />
                                    </td>
                                )}
                            <td></td>
                        </tr>

                        <PFTResultParameters
                            isLoading={acceptabilityState.loadingBestValues}
                            results={resultData}
                            isRejected={isRejected}
                            studyRelevantParameters={studyLungVolumeRelevantParameters}
                            showConfiguredParametersOnly={true}
                        />

                        <RepeatabilityTable
                            isLoading={acceptabilityState.loadingBestValues}
                            results={resultData}
                            isRejected={isRejected}
                            studyRelevantParameters={studyLungVolumeRelevantParameters}
                            showConfiguredParametersOnly={true}
                        />

                        <TestAcceptabilityTable
                            isViewOnly={isViewOnly}
                            isLoading={acceptabilityState.loadingBestValues}
                            testResults={acceptabilityState.spirometryResults.TestResults}
                            displayTechnicianAcceptability={!isSiteAnalysisSet}
                            onAcceptabilityChange={(id, checked) =>
                                acceptabilityDispatch(
                                    ToggleTestAcceptabilityAction.create(id, checked)
                                )
                            }
                        />
                        <OverreadingResultsComments
                            viewOnly={isViewOnly}
                            session={session}
                            showTechnician={false}
                            fixedCommentsDispatchOverreading={fixedCommentsDispatchOverreading}
                            fixedCommentsStateOverreading={fixedCommentsStateOverreading}
                        />

                    </tbody>
                </table>
            </ResultsContext.Provider>
        </>
    );
}
